import React from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, SkeletonRows, EmptyState } from "@/components/dash/ui";
import { AgreementTypeBadge } from "@/components/dash/AgreementBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Archive, Edit3, LayoutTemplate } from "lucide-react";

const TYPES = ["nda", "sla", "msa"];
const PTYPES = ["client", "vendor", "staffing"];

function TemplateDialog({ open, onClose, template, clauses, onSaved }) {
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const editing = !!template;
  React.useEffect(() => {
    if (!open) return;
    setForm(template ? { ...template, sections: template.sections || [] }
      : { title: "", agreement_type: "nda", description: "", partner_types: [...PTYPES], sections: [{ title: "Section 1", clause_keys: [], order: 1 }], legal_note: "" });
  }, [open]);
  const togglePtype = (t) => setForm((f) => ({ ...f, partner_types: (f.partner_types || []).includes(t) ? f.partner_types.filter((x) => x !== t) : [...(f.partner_types || []), t] }));
  const addSection = () => setForm((f) => ({ ...f, sections: [...(f.sections || []), { title: `Section ${(f.sections?.length || 0) + 1}`, clause_keys: [], order: (f.sections?.length || 0) + 1 }] }));
  const updateSection = (i, patch) => setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const removeSection = (i) => setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));
  const toggleClause = (i, key) => setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => idx === i ? { ...s, clause_keys: s.clause_keys.includes(key) ? s.clause_keys.filter((k) => k !== key) : [...s.clause_keys, key] } : s) }));

  const availableClauses = (clauses || []).filter((c) => c.active && (c.applicable_types || []).includes(form.agreement_type));

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title is required");
    const payload = { title: form.title, agreement_type: form.agreement_type, description: form.description, partner_types: form.partner_types, sections: form.sections.map((s, i) => ({ title: s.title, clause_keys: s.clause_keys, order: i + 1 })), legal_note: form.legal_note };
    setBusy(true);
    try {
      if (editing) await api.patch(`/admin/agreements/templates/${template.key}`, payload);
      else await api.post("/admin/agreements/templates", payload);
      toast.success(editing ? "Template updated" : "Template created"); onClose(); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle>
          <DialogDescription>Compose an agreement template by grouping clauses into ordered sections.</DialogDescription></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="template-title" /></div>
            <div><Label>Agreement Type</Label>
              <Select value={form.agreement_type} onValueChange={(v) => setForm({ ...form, agreement_type: v })}>
                <SelectTrigger className="mt-1" data-testid="template-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Description</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="template-description" /></div>
          <div><Label>Applicable Partner Types</Label>
            <div className="flex gap-2 mt-2">{PTYPES.map((t) => (
              <button key={t} type="button" onClick={() => togglePtype(t)} data-testid={`template-ptype-${t}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold border capitalize ${(form.partner_types || []).includes(t) ? "bg-vs-royal text-white border-vs-royal" : "bg-white text-slate-500 border-slate-200"}`}>{t}</button>
            ))}</div></div>
          <div>
            <div className="flex items-center justify-between mb-2"><Label>Sections & Clauses</Label>
              <Button size="sm" variant="outline" onClick={addSection} data-testid="add-section"><Plus className="h-3.5 w-3.5 mr-1" />Section</Button></div>
            <div className="space-y-3">{(form.sections || []).map((s, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`section-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Input value={s.title} onChange={(e) => updateSection(i, { title: e.target.value })} className="h-8 text-sm" data-testid={`section-title-${i}`} />
                  <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => removeSection(i)} data-testid={`section-remove-${i}`}>Remove</Button>
                </div>
                <div className="flex flex-wrap gap-1.5">{availableClauses.map((c) => (
                  <button key={c.key} type="button" onClick={() => toggleClause(i, c.key)} data-testid={`section-${i}-clause-${c.key}`}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-medium border ${s.clause_keys.includes(c.key) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-500 border-slate-200"}`}>{c.title}</button>
                ))}{availableClauses.length === 0 && <span className="text-xs text-slate-400">No clauses for this type. Add some in the Clause Library.</span>}</div>
              </div>
            ))}</div>
          </div>
          <div><Label>Legal Note</Label><Textarea value={form.legal_note || ""} onChange={(e) => setForm({ ...form, legal_note: e.target.value })} rows={2} className="mt-1 text-xs" data-testid="template-legal-note" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-4"><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-vs-royal text-white" data-testid="template-save">{editing ? "Save" : "Create"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export default function AgreementTemplates() {
  const [items, setItems] = React.useState(null);
  const [clauses, setClauses] = React.useState([]);
  const [dialog, setDialog] = React.useState({ open: false, template: null });

  const load = React.useCallback(() => {
    api.get("/admin/agreements/templates").then((r) => setItems(r.data.items || [])).catch((e) => { setItems([]); toast.error(apiError(e.response?.data?.detail)); });
    api.get("/admin/agreements/clauses").then((r) => setClauses(r.data.items || [])).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const archive = (t) => api.delete(`/admin/agreements/templates/${t.key}`).then(() => { toast.success("Template archived"); load(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));

  return (
    <div>
      <PageHeader title="Agreement Templates" description="Composable NDA, SLA and MSA templates built from the clause library." breadcrumb="VS Command Center · Agreements · Templates">
        <Button className="bg-vs-royal text-white" onClick={() => setDialog({ open: true, template: null })} data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1" />New Template</Button>
      </PageHeader>

      {items === null ? <SkeletonRows rows={5} /> : items.length === 0 ? (
        <EmptyState icon="LayoutTemplate" title="No templates" />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="template-list">
          {items.map((t) => (
            <Card key={t.key} className={`!p-4 ${t.status !== "active" ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-vs-light flex items-center justify-center shrink-0"><LayoutTemplate className="h-4 w-4 text-vs-royal" /></div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="font-semibold text-slate-800">{t.title}</span><AgreementTypeBadge type={t.agreement_type} /></div>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</p>
                    <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-400">
                      <span>{(t.sections || []).length} sections</span><span>·</span>
                      <span>{(t.sections || []).reduce((n, s) => n + (s.clause_keys?.length || 0), 0)} clauses</span><span>·</span><span>v{t.version}</span>
                    </div>
                    <div className="flex gap-1 mt-1.5 flex-wrap">{(t.partner_types || []).map((p) => <span key={p} className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 capitalize">{p}</span>)}</div>
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, template: t })} data-testid={`template-edit-${t.key}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                  {t.status === "active" && <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" onClick={() => archive(t)} data-testid={`template-archive-${t.key}`}><Archive className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <TemplateDialog open={dialog.open} onClose={() => setDialog({ open: false, template: null })} template={dialog.template} clauses={clauses} onSaved={load} />
    </div>
  );
}
